import { useCallback, useEffect, useState } from 'react';

import { arquivosDoArtista, lerTexto, linkParaTocar, type ArquivoDoPerfil } from '../services/db/arquivos';

// O arquivo que a Nyta citou, resolvido para o cartão da conversa (web e app).
//
// A lista do perfil é pedida UMA vez por perfil e reaproveitada por todos os cartões da tela: uma
// resposta que mostra seis faixas não pode virar seis chamadas iguais. Ela vence em 30 segundos,
// que é o bastante para um arquivo que a Nyta acabou de criar aparecer no cartão seguinte.

const VALIDADE_MS = 30_000;
const listas = new Map<string, { em: number; lista: Promise<ArquivoDoPerfil[]> }>();

/** A lista do perfil; com `novaDesde`, só serve a que foi pedida a partir desse instante. */
function listaDoPerfil(artistId: string, novaDesde = 0): Promise<ArquivoDoPerfil[]> {
  const guardada = listas.get(artistId);
  if (guardada && Date.now() - guardada.em < VALIDADE_MS && guardada.em >= novaDesde) return guardada.lista;
  const lista = arquivosDoArtista(artistId).catch((e) => {
    listas.delete(artistId);
    throw e;
  });
  listas.set(artistId, { em: Date.now(), lista });
  return lista;
}

/** Só para testes: esquece as listas guardadas. */
export const esquecerArquivosDaConversa = () => listas.clear();

// ─── Quando a Nyta mexe nos arquivos ──────────────────────────────────────────
//
// O Storage não passa pelo tempo real (só as tabelas da publicação), então a tela de Arquivos
// aberta atrás do chat não saberia que a Nyta renomeou ou apagou algo. O chat avisa ao terminar
// uma ação confirmada, e quem ouve relê. Também esquece a lista guardada acima: o cartão do
// arquivo que acabou de nascer tem de achá-lo.
const ouvintes = new Set<(artistId: string) => void>();

export function avisarQueArquivosMudaram(artistId: string): void {
  listas.delete(artistId);
  ouvintes.forEach((f) => f(artistId));
}

export function useQuandoArquivosMudarem(artistId: string | null | undefined, aoMudar: () => void): void {
  useEffect(() => {
    if (!artistId) return undefined;
    const ouvir = (id: string) => { if (id === artistId) aoMudar(); };
    ouvintes.add(ouvir);
    return () => { ouvintes.delete(ouvir); };
  }, [artistId, aoMudar]);
}

export type EstadoDoArquivoNaConversa =
  | { estado: 'carregando' }
  | { estado: 'sumiu' }
  | { estado: 'pronto'; arquivo: ArquivoDoPerfil; link: string | null; trecho: string | null };

const ehTexto = (a: ArquivoDoPerfil) => a.balde === 'arquivos' && /\.(txt|md)$/i.test(a.nome);

export function useArquivoDaConversa(artistId: string | null | undefined, caminho: string): EstadoDoArquivoNaConversa {
  const [estado, setEstado] = useState<EstadoDoArquivoNaConversa>({ estado: 'carregando' });
  // O cartão que nasce na resposta de uma ação pode ler a lista antes de o arquivo novo entrar
  // nela: o aviso do chat, no fim da ação, faz ele procurar de novo.
  const [rodada, setRodada] = useState(0);
  const deNovo = useCallback(() => setRodada((r) => r + 1), []);
  useQuandoArquivosMudarem(artistId, deNovo);

  useEffect(() => {
    let vivo = true;
    // Outro perfil nunca: o caminho começa pelo id do artista, e a conversa é de um perfil só.
    if (!artistId || !caminho.startsWith(`${artistId}/`)) {
      setEstado({ estado: 'sumiu' });
      return undefined;
    }
    const desde = Date.now();
    (async () => {
      try {
        let arquivo = (await listaDoPerfil(artistId)).find((a) => a.caminho === caminho);
        // A lista guardada pode ser de antes de o arquivo nascer: um relatório da Nyta é leitura,
        // não ação confirmada, e não passa pelo aviso (04/10/2026, o CSV aparecia como apagado).
        // Procura de novo, uma vez, numa lista pedida agora.
        if (!arquivo) arquivo = (await listaDoPerfil(artistId, desde)).find((a) => a.caminho === caminho);
        if (!vivo) return;
        if (!arquivo) { setEstado({ estado: 'sumiu' }); return; }
        const [link, trecho] = await Promise.all([
          linkParaTocar(arquivo).catch(() => null),
          ehTexto(arquivo) ? lerTexto(arquivo).then((t) => t.slice(0, 600)).catch(() => null) : Promise.resolve(null),
        ]);
        if (vivo) setEstado({ estado: 'pronto', arquivo, link, trecho });
      } catch {
        if (vivo) setEstado({ estado: 'sumiu' });
      }
    })();
    return () => { vivo = false; };
  }, [artistId, caminho, rodada]);

  return estado;
}
